import * as React from "react";
import { useQueries } from "@tanstack/react-query";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useNavigate } from "react-router-dom";
import DialogContext from "../../contexts/DialogContext";
import { useAlerts } from "../../hooks/query/useAlerts";
import { useIntegrations } from "../../hooks/query/useIntegrations";
import { alertSchemaService } from "../../services/alertSchemasService";
import { COLORS } from "../../theme/theme-options";
import { ColumnDef, DataTable } from "../../components/Table";
import type { Alert, AlertSchema } from "../../types";
import { DeleteButton } from "../../components/buttons/DeleteButton";
import RowActionsMenu from "../../components/RowActionsMenu";
import AlertSchemaDetailsDialog from "../../components/dialogs/AlertSchemaDetailsDialog";

const AlertSchemaCell: React.FC<{
  integrationId: string;
  integrationSchemaIdByIntegrationId: Map<string, string>;
  alertSchemaById: Map<string, AlertSchema>;
  loadingAlertSchemaIds: Set<string>;
  isIntegrationsLoading: boolean;
  onSelectAlertSchema: (schema: AlertSchema) => void;
}> = ({
  integrationId,
  integrationSchemaIdByIntegrationId,
  alertSchemaById,
  loadingAlertSchemaIds,
  isIntegrationsLoading,
  onSelectAlertSchema,
}) => {
  if (isIntegrationsLoading) {
    return <Skeleton variant="rounded" width="88%" height={24} />;
  }

  const alertSchemaId = integrationSchemaIdByIntegrationId.get(integrationId);
  if (!alertSchemaId) {
    return "Schema unavailable";
  }

  if (loadingAlertSchemaIds.has(alertSchemaId)) {
    return <Skeleton variant="rounded" width="88%" height={24} />;
  }

  const alertSchema = alertSchemaById.get(alertSchemaId);
  if (!alertSchema) {
    return "Schema unavailable";
  }

  return (
    <Button
      size="small"
      variant="text"
      onClick={(event) => {
        event.stopPropagation();
        onSelectAlertSchema(alertSchema);
      }}
      sx={{
        px: 0,
        minWidth: 0,
        justifyContent: "flex-start",
        textTransform: "none",
        maxWidth: "100%",
      }}
    >
      {alertSchema.name}
    </Button>
  );
};

const AlertsConfigPage: React.FC = () => {
  const { data: alerts = [], isLoading } = useAlerts();
  const { data: integrations = [], isLoading: isIntegrationsLoading } =
    useIntegrations();
  const { show } = React.useContext(DialogContext);
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [selectedAlertSchema, setSelectedAlertSchema] =
    React.useState<AlertSchema | null>(null);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    alert: Alert;
  } | null>(null);

  const integrationSchemaIdByIntegrationId = React.useMemo(() => {
    const byIntegrationId = new Map<string, string>();
    for (const integration of integrations) {
      byIntegrationId.set(integration.id, integration.alertSchemaId);
    }
    return byIntegrationId;
  }, [integrations]);

  const alertSchemaIds = React.useMemo(() => {
    const schemaIds = new Set<string>();

    for (const alert of alerts) {
      const schemaId = integrationSchemaIdByIntegrationId.get(alert.integrationId);
      if (!schemaId) {
        continue;
      }
      schemaIds.add(schemaId);
    }

    return Array.from(schemaIds);
  }, [alerts, integrationSchemaIdByIntegrationId]);

  const alertSchemaQueries = useQueries({
    queries: alertSchemaIds.map((alertSchemaId) => ({
      queryKey: ["alert-provider", alertSchemaId],
      queryFn: async () => alertSchemaService.get(alertSchemaId),
    })),
  });

  const alertSchemaById = React.useMemo(() => {
    const byId = new Map<string, AlertSchema>();

    alertSchemaQueries.forEach((query, index) => {
      const alertSchema = query.data;
      if (!alertSchema) {
        return;
      }
      byId.set(alertSchemaIds[index], alertSchema);
    });

    return byId;
  }, [alertSchemaIds, alertSchemaQueries]);

  const loadingAlertSchemaIds = React.useMemo(() => {
    const loadingIds = new Set<string>();

    alertSchemaQueries.forEach((query, index) => {
      if (query.isLoading || query.isFetching) {
        loadingIds.add(alertSchemaIds[index]);
      }
    });

    return loadingIds;
  }, [alertSchemaIds, alertSchemaQueries]);

  const columns: ColumnDef<Alert>[] = React.useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        width: "23%",
        render: (row) => {
          return row.name;
        },
      },
      {
        id: "description",
        header: "Description",
        width: "16%",
        render: (row) => {
          return row.description ?? "";
        },
      },
      {
        id: "providerAlertId",
        header: "Provider alert id",
        width: "21%",
        render: (row) => {
          return row.foreignKeyValue;
        },
      },
      {
        id: "alertSchema",
        header: "Alert schema",
        width: "22%",
        render: (row) => {
          return (
            <AlertSchemaCell
              integrationId={row.integrationId}
              integrationSchemaIdByIntegrationId={integrationSchemaIdByIntegrationId}
              alertSchemaById={alertSchemaById}
              loadingAlertSchemaIds={loadingAlertSchemaIds}
              isIntegrationsLoading={isIntegrationsLoading}
              onSelectAlertSchema={(schema) => {
                setSelectedAlertSchema(schema);
              }}
            />
          );
        },
      },
    ],
    [
      alertSchemaById,
      integrationSchemaIdByIntegrationId,
      isIntegrationsLoading,
      loadingAlertSchemaIds,
    ],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2.2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.grey[100] }}>
            Alerts
          </Typography>
          <Typography sx={{ color: COLORS.grey[500], mt: 0.2 }}>
            Imported and connected alert definitions
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon fontSize="small" />}
            onClick={() => {
              show({ dialog: "new-alert" });
            }}
          >
            New
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileRoundedIcon fontSize="small" />}
            onClick={() => {
              show({ dialog: "import-alerts" });
            }}
          >
            Import Alerts
          </Button>
          <DeleteButton
            disabled={selectedIds.length === 0}
            onClick={() => {
              if (selectedIds.length === 0) {
                return;
              }
              show({
                dialog: "confirm-delete",
                entity: "alert",
                ids: selectedIds.join(","),
              });
            }}
          />
        </Stack>
      </Stack>

      <DataTable
        title="All alerts"
        loading={isLoading}
        rows={alerts}
        columns={columns}
        colSpan={6}
        onSelectionChange={setSelectedIds}
        onRowClick={(row) => {
          navigate(`/alerts/${row.id}`);
        }}
        onRowMenuClick={(alert, anchorEl) => {
          setRowMenu({
            alert,
            anchorEl,
          });
        }}
      />

      <RowActionsMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        onEdit={() => {
          const alertId = rowMenu?.alert.id;
          if (!alertId) {
            return;
          }
          show({
            dialog: "edit-alert",
            alertId,
          });
        }}
      />

      <AlertSchemaDetailsDialog
        alertSchema={selectedAlertSchema}
        onClose={() => {
          setSelectedAlertSchema(null);
        }}
      />
    </Box>
  );
};

export default AlertsConfigPage;
